import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { GraphicTypeEnum } from "../../../services/redux/types/CartLoyaltyV2Types";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import {
  EntityImageLookup,
  OfferIconSlug,
  OfferIconUi,
  OfferUiConfigResolved,
} from "../../../utils/loyaltyTypes";
import { BundleOfferImages } from "../../screens/S-Cart/ItemList/bundleItem/bundleItemImage";
import styles from "../GeneralOfferIcon/GeneralOfferIcon.module.scss";
import { TwistIcon } from "../Icons/Icons";
import { FreeItemIcon, OfferIcon } from "../Icons/loyalty.icons";
import { LoyaltyGraphicIcon } from "./LoyaltyGraphicIcon";

type LoyaltyOfferGraphic = OfferUiConfigResolved["graphics"][number];

type LoyaltyOfferIconProps = {
  offer: LoyaltyOffer;
  sliderStyle?: boolean;
  backgroundIconColor?: "primary" | "loyalty";
  largeIcons?: boolean;
  twist?: boolean;
  width?: number;
  fontStyle?: React.CSSProperties;
  giftIcon?: boolean;
  useGraphics?: boolean;
  /**
   * Render the offer's icon badge even for a BOGO or bundle, which otherwise
   * take over the badge with their item collage.
   */
  iconOnly?: boolean;
};

const defaultIconGraphic: OfferIconUi = {
  type: "icon",
  value: OfferIconSlug.Gift,
};

type LegacyBundleGraphic = { type: GraphicTypeEnum; value: string };

/** Maps each loyalty `uiConfig.graphics` entry to legacy bundle slot (URL, icon slug, or text). */
const loyaltyGraphicToBundleSlot = (
  graphic: LoyaltyOfferGraphic,
  entityImageLookup: EntityImageLookup
): LegacyBundleGraphic | undefined => {
  if (graphic.type === "image") {
    const imageUrl = entityImageLookup[graphic.entityId]?.itemImage;

    return imageUrl
      ? { type: GraphicTypeEnum.IMAGE, value: imageUrl }
      : undefined;
  }

  if (graphic.type === "icon") {
    return { type: GraphicTypeEnum.ICON, value: String(graphic.value) };
  }

  if (graphic.type === "text") {
    return { type: GraphicTypeEnum.TEXT, value: graphic.value };
  }

  return undefined;
};

/** Maps loyalty icon/graphic to `BundleOfferImages` shape (circular badge). */
const loyaltyDisplayToBundleGraphic = (
  graphic: LoyaltyOfferGraphic | OfferIconUi,
  entityImageLookup: EntityImageLookup
): LegacyBundleGraphic | undefined => {
  if (graphic.type === "image" && "entityId" in graphic) {
    const imageUrl = entityImageLookup[graphic.entityId]?.itemImage;

    return imageUrl
      ? { type: GraphicTypeEnum.IMAGE, value: imageUrl }
      : undefined;
  }

  if (graphic.type === "icon") {
    return { type: GraphicTypeEnum.ICON, value: String(graphic.value) };
  }

  if (graphic.type === "text") {
    return { type: GraphicTypeEnum.TEXT, value: graphic.value };
  }

  return undefined;
};

// const getMultiplierLabel = (offer: LoyaltyOffer): string => {
//   const textGraphic = offer.uiConfig?.graphics?.find(
//     (graphic) => graphic.type === "text"
//   );

//   if (textGraphic?.type === "text") {
//     return textGraphic.value;
//   }

//   return `x${offer.value}`;
// };

export const LoyaltyOfferIcon = ({
  offer,
  sliderStyle,
  backgroundIconColor,
  largeIcons,
  twist,
  width,
  fontStyle,
  giftIcon = false,
  useGraphics = false,
  iconOnly = false,
}: LoyaltyOfferIconProps) => {
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );

  // if (offer.isMultiplierOffer()) {
  //   return (
  //     <div className={sliderStyle ? styles.slider : ""}>
  //       <div
  //         className={`${styles.multiplier_icon} ${
  //           largeIcons && styles.large
  //         }`}
  //         style={{ width: `${width}px`, height: `${width}px` }}
  //       >
  //         <span style={fontStyle}>{getMultiplierLabel(offer)}</span>
  //         <MileStone hasFullWidth={!!width} />
  //       </div>
  //     </div>
  //   );
  // }

  const icon =
    (useGraphics && !offer.isItemFromListOffer()
      ? offer.getGraphic()
      : offer.getIcon()) ?? defaultIconGraphic;

  /** Circular bundle/BOGO badge uses `uiConfig.icon` when not in graphics mode. */
  const bundleIconForCircular = offer.uiConfig?.icon ?? defaultIconGraphic;

  const isBogoOrBundle = offer.isBogoOffer() || offer.isBundleOffer();

  const resolvedLoyaltyBundleGraphics = (offer.uiConfig?.graphics ?? [])
    .map((itemGraphic) =>
      loyaltyGraphicToBundleSlot(itemGraphic, entityImageLookup)
    )
    .filter(Boolean) as LegacyBundleGraphic[];

  const bundleGraphicsForView = sliderStyle
    ? resolvedLoyaltyBundleGraphics.slice(0, 1)
    : resolvedLoyaltyBundleGraphics;

  const circularFromResolvedGraphics =
    useGraphics && bundleGraphicsForView.length > 0
      ? bundleGraphicsForView
      : [];

  const graphicForCircularFallback = useGraphics ? icon : bundleIconForCircular;

  const fallbackBundleGraphic =
    loyaltyDisplayToBundleGraphic(
      graphicForCircularFallback,
      entityImageLookup
    ) ??
    (useGraphics
      ? loyaltyDisplayToBundleGraphic(
          offer.getIcon() ?? defaultIconGraphic,
          entityImageLookup
        )
      : loyaltyDisplayToBundleGraphic(defaultIconGraphic, entityImageLookup));

  const circularBundleGraphics: LegacyBundleGraphic[] =
    circularFromResolvedGraphics.length > 0
      ? circularFromResolvedGraphics
      : isBogoOrBundle && fallbackBundleGraphic
      ? [fallbackBundleGraphic]
      : [];

  const shouldRenderBundle =
    !iconOnly && isBogoOrBundle && circularBundleGraphics.length > 0;

  return (
    <div className={sliderStyle ? styles.slider : ""}>
      <div
        className={`${styles.offer_label} ${largeIcons && styles.large}`}
        style={{ width: `${width}px`, height: `${width}px` }}
      >
        <div
          className={`${styles.flip_animation} ${
            twist && !shouldRenderBundle ? styles.animate : ""
          }`}
        >
          <div
            className={
              twist && !shouldRenderBundle ? styles.front : styles.center
            }
          >
            {!shouldRenderBundle ? (
              <>
                <div
                  className={`${styles.offer_label_background} ${
                    backgroundIconColor ? styles[backgroundIconColor] : ""
                  } ${largeIcons && styles.large}`}
                >
                  {<OfferIcon />}
                </div>
                <div
                  className={`${styles.offer_svg} ${
                    largeIcons && styles.large
                  }`}
                  style={icon?.type === "icon" ? fontStyle : undefined}
                >
                  {giftIcon ? (
                    <FreeItemIcon />
                  ) : (
                    <LoyaltyGraphicIcon
                      graphic={icon ?? defaultIconGraphic}
                      offerUiGraphics={
                        useGraphics && !offer.isItemFromListOffer()
                          ? offer.uiConfig?.graphics
                          : undefined
                      }
                      largeIcons={largeIcons}
                      fallbackIcon={defaultIconGraphic}
                      style={fontStyle}
                    />
                  )}
                </div>
              </>
            ) : (
              <BundleOfferImages
                large={largeIcons}
                graphics={circularBundleGraphics}
                loyaltyGraphics
                transparentBg={true}
              />
            )}
          </div>

          <div className={styles.back}>
            <div
              className={`${styles.offer_label_background} ${styles.twist} ${
                largeIcons && styles.large
              }`}
            >
              <OfferIcon />
            </div>
            <div
              className={`${styles.offer_svg} ${
                largeIcons && styles.large_twist
              } ${styles.twist}`}
            >
              <TwistIcon />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
